import React, { useEffect, useState } from "react";
import { Box, CircularProgress, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import { createClientReference, updateClientReference } from "../gestionCarteraApi";
import { formatDocument, formatFullName } from "../gestionCarteraFormat";
import { COLORS, labelSx, ModalShell, PrimaryButton, SecondaryButton } from "../gestionCarteraUi";

const FIELDS = [
  { name: "name", label: "Nombre" },
  { name: "lastName", label: "Apellido" },
  { name: "cellphone", label: "Celular" },
  { name: "email", label: "Correo" },
];

const EMPTY_REFERENCE = { uuid: null, name: "", lastName: "", cellphone: "", email: "" };

const toRow = (reference) =>
  FIELDS.reduce((row, field) => ({ ...row, [field.name]: reference[field.name] || "" }), {
    uuid: reference.uuid,
    cellphoneCountryCode: reference.cellphoneCountryCode,
  });

const isFilled = (row) => row.name.trim() && row.cellphone.trim();

/**
 * M5 · Editar referencias. Solo crear y editar: el backend no expone DELETE
 * (decisión de producto: no se elimina desde este módulo).
 */
export default function ReferenciasModal({ open, client, references, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setRows(references.length ? references.map(toRow) : [{ ...EMPTY_REFERENCE }]);
  }, [open, references]);

  const updateRow = (index, name, value) =>
    setRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, [name]: value } : row)));

  const handleSave = async () => {
    setSaving(true);
    const pending = rows.filter(isFilled).map((row) => {
      const clientReferenceRequest = { ...row, clientUuid: client.uuid };
      delete clientReferenceRequest.uuid;
      return row.uuid
        ? updateClientReference(row.uuid, clientReferenceRequest)
        : createClientReference(clientReferenceRequest);
    });
    const results = await Promise.allSettled(pending);
    setSaving(false);
    if (results.every((result) => result.status === "fulfilled")) {
      onSaved();
      Swal.fire({ icon: "success", title: "Referencias guardadas", confirmButtonColor: COLORS.primary });
      return;
    }
    Swal.fire({ icon: "error", title: "Error", text: "No se pudieron guardar todas las referencias.", confirmButtonColor: COLORS.primary });
  };

  return (
    <ModalShell
      open={open}
      onClose={saving ? undefined : onClose}
      title="Editar referencias"
      subtitle={client ? `${formatFullName(client)} · ${formatDocument(client)}` : ""}
      maxWidth="md"
      actions={
        <>
          <SecondaryButton onClick={onClose} disabled={saving}>Cancelar</SecondaryButton>
          <PrimaryButton
            onClick={handleSave}
            disabled={saving || !rows.some(isFilled)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Guardar cambios
          </PrimaryButton>
        </>
      }
    >
      <Box sx={{ display: { xs: "none", sm: "grid" }, gridTemplateColumns: "repeat(4, 1fr)", gap: 1.5 }}>
        {FIELDS.map((field) => <Box key={field.name} sx={labelSx}>{field.label}</Box>)}
      </Box>
      {rows.map((row, index) => (
        <Box key={row.uuid || `new-${index}`} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" }, gap: 1.5 }}>
          {FIELDS.map((field) => (
            <TextField
              key={field.name}
              size="small"
              value={row[field.name]}
              onChange={(event) => updateRow(index, field.name, event.target.value)}
              inputProps={{ "aria-label": `${field.label}, referencia ${index + 1}` }}
            />
          ))}
        </Box>
      ))}
      <Box>
        <SecondaryButton startIcon={<AddIcon />} onClick={() => setRows((current) => [...current, { ...EMPTY_REFERENCE }])}>
          Agregar referencia
        </SecondaryButton>
      </Box>
    </ModalShell>
  );
}
